import React from 'react';
import { FileText, Plus, X } from 'lucide-react';

export interface DocumentTabItem {
  id: string;
  title: string;
  active: boolean;
  /** Slice 1.3b: für Multi-Dokument-Synthese markiert */
  synthesisSelected?: boolean;
}

export interface DocumentTabsBarProps {
  documents: DocumentTabItem[];
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
  onAddNew: () => void;
  addNewDisabled?: boolean;
  addNewTitle?: string;
  /** Checkboxen nur bei 2+ offenen Tabs */
  showSynthesisSelect?: boolean;
  onToggleSynthesis?: (id: string) => void;
  synthesisSelectDisabled?: boolean;
}

/**
 * Browser-/IDE-artige Tab-Leiste fuer mehrere gleichzeitig geoeffnete
 * OnlyOffice-Dokumente. Es ist immer nur ein Editor-Embed aktiv (Tab-Wechsel
 * = `alphaDocSource`-Wechsel im `CustomerPortal`, gleicher `document.key` →
 * DS-Cache). Diese Komponente ist bewusst entkoppelt: sie kennt nur
 * vorbereitete `{ id, title, active }`-Items, nicht den Source-Typ.
 */
export const DocumentTabsBar: React.FC<DocumentTabsBarProps> = ({
  documents,
  onSelect,
  onClose,
  onAddNew,
  addNewDisabled = false,
  addNewTitle = 'Weiteres Dokument öffnen',
  showSynthesisSelect = false,
  onToggleSynthesis,
  synthesisSelectDisabled = false,
}) => {
  if (documents.length === 0) return null;
  const synthesisEnabled = showSynthesisSelect && Boolean(onToggleSynthesis);
  return (
    <div
      className="flex items-center gap-1 overflow-x-auto p-1 rounded-lg bg-white/[0.04] border border-white/[0.06] shrink-0"
      role="tablist"
      aria-label="Geöffnete Dokumente"
    >
      {documents.map((doc) => (
        <div
          key={doc.id}
          className={`group inline-flex items-center gap-1 rounded-md pl-2.5 pr-1 py-1.5 text-sm font-medium transition-colors min-w-0 ${
            doc.active ? 'bg-white/[0.08] text-white' : 'text-slate-500 hover:text-slate-300'
          }`}
        >
          {synthesisEnabled ? (
            <input
              type="checkbox"
              checked={Boolean(doc.synthesisSelected)}
              disabled={synthesisSelectDisabled && !doc.synthesisSelected}
              onChange={(e) => {
                e.stopPropagation();
                onToggleSynthesis?.(doc.id);
              }}
              onClick={(e) => e.stopPropagation()}
              className="h-3.5 w-3.5 flex-shrink-0 rounded border-white/20 bg-slate-900/60 text-violet-500 focus:ring-violet-400/40 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label={`„${doc.title}" für ARKO-Synthese auswählen`}
              title="Für ARKO-Synthese auswählen"
            />
          ) : null}
          <button
            type="button"
            role="tab"
            aria-selected={doc.active}
            onClick={() => onSelect(doc.id)}
            className="inline-flex items-center gap-1.5 min-w-0"
            title={doc.title}
          >
            <FileText className="w-3.5 h-3.5 flex-shrink-0 text-blue-400/80" aria-hidden />
            <span className="truncate max-w-[120px] sm:max-w-[180px]">{doc.title}</span>
          </button>
          <button
            type="button"
            onClick={() => onClose(doc.id)}
            className="flex-shrink-0 rounded p-0.5 text-slate-500 hover:text-slate-200 hover:bg-white/[0.1] transition-colors"
            aria-label={`„${doc.title}" schließen`}
            title="Tab schließen"
          >
            <X className="w-3 h-3" aria-hidden />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={onAddNew}
        disabled={addNewDisabled}
        className="flex-shrink-0 inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-200 hover:bg-white/[0.06] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-500"
        aria-label={addNewTitle}
        title={addNewTitle}
      >
        <Plus className="w-3.5 h-3.5" aria-hidden />
      </button>
    </div>
  );
};
